import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { DoctorAPI, DoctorFull, DoctorUpdatePayload } from "../../services/doctor.service";
import { SpecializationTagInput } from "../ui/SpecializationTagInput";

import { DEPARTMENTS } from "../../constants/doctor.constants";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const STATUSES = ["Present", "Break", "On Leave", "Absent", "Not Available"];

// ── Type compatibility shim ───────────────────────────────────────────────────
// DoctorProfileModal (Sudhanshu's component) passes his camelCase Doctor type.
// Our modal uses the backend DoctorFull (snake_case). We accept either and
// normalise to a flat object the form can read without caring which branch it came from.

interface DoctorLike {
  // backend DoctorFull fields (snake_case)
  doctor_id?: string;
  name?: string;
  specialization?: string;
  specializations?: string[];   // TEXT[] — present after Alembic migration ships
  registration_number?: string | null;
  qualifications?: string | null;
  experience_years?: number | null;
  consultation_fee?: number | null;
  room_location?: string | null;
  availability_schedules?: {
    day_of_week: number;
    start_time: string;
    end_time: string;
    slot_duration_minutes: number;
  }[];
  // Sudhanshu's Doctor schema fields (camelCase)
  userId?: string;
  firstName?: string;
  lastName?: string;
  specialty?: string;
  qualification?: string;
  experienceYears?: number;
  consultationFee?: number;
  room?: string;
  // shared
  email?: string | null;
  phone?: string | null;
  department?: string;
  status?: string;
  email_locked?: boolean;
}

function normalise(d: DoctorLike) {
  return {
    id:                d.doctor_id || d.userId || "",
    name:              d.name || `${d.firstName ?? ""} ${d.lastName ?? ""}`.trim(),
    email:             d.email || "",
    phone:             d.phone || "",
    specialization:    d.specialization || d.specialty || "",
    // Prefer new array field; fall back to splitting old comma string for backward compat
    specializations:   d.specializations?.length
                         ? d.specializations
                         : (d.specialization || d.specialty || "")
                             .split(",").map(s => s.trim()).filter(Boolean),
    department:        d.department || "",
    registration_number: d.registration_number || "",
    qualifications:    d.qualifications || d.qualification || "",
    experience_years:  d.experience_years != null
                         ? String(d.experience_years)
                         : d.experienceYears != null
                         ? String(d.experienceYears)
                         : "",
    consultation_fee:  d.consultation_fee != null
                         ? String(d.consultation_fee)
                         : d.consultationFee != null
                         ? String(d.consultationFee)
                         : "",
    room_location:     d.room_location || d.room || "",
    status:            d.status || "Present",
    availability_schedules: d.availability_schedules || [],
  };
}

// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  isOpen: boolean;
  // Accepts either backend DoctorFull or Sudhanshu's camelCase Doctor type
  doctor: DoctorFull | DoctorLike;
  onClose: () => void;
  onSuccess: () => void;
}

interface DaySlot {
  start_time: string;
  end_time: string;
  slot_duration_minutes: number;
}

interface ScheduleRow {
  day_of_week: number;
  enabled: boolean;
  slots: DaySlot[];
}

const toHHMM = (t: string) => t.slice(0, 5); // "09:00:00" → "09:00"

export const EditDoctorModal: React.FC<Props> = ({ isOpen, doctor, onClose, onSuccess }) => {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", specializations: [] as string[], department: "",
    registration_number: "", qualifications: "", experience_years: "",
    consultation_fee: "", room_location: "", status: "Present",
  });
  const [schedules, setSchedules] = useState<ScheduleRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Callers may pass a list/card shape without contact or MCI fields (e.g. the
  // camelCase Doctor from DoctorCard), so the full record is always fetched on open.
  const [full, setFull] = useState<DoctorLike | null>(null);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    if (!isOpen) { setFull(null); return; }
    const id = normalise(doctor as DoctorLike).id;
    if (!id) return;
    let cancelled = false;
    setFetching(true);
    DoctorAPI.get(id)
      .then(res => { if (!cancelled) setFull(res); })
      .catch(() => { if (!cancelled) toast.error("Failed to load latest doctor details"); })
      .finally(() => { if (!cancelled) setFetching(false); });
    return () => { cancelled = true; };
  }, [isOpen, doctor]);

  const source = (full ?? doctor) as DoctorLike;
  const emailLocked = !!source.email_locked;

  useEffect(() => {
    if (!isOpen) return;
    const d = normalise(source);
    setForm({
      name:                d.name,
      email:               d.email,
      phone:               d.phone,
      specializations:      d.specializations,
      department:          d.department,
      registration_number: d.registration_number,
      qualifications:      d.qualifications,
      experience_years:    d.experience_years,
      consultation_fee:    d.consultation_fee,
      room_location:       d.room_location,
      status:              d.status,
    });
    const byDay: Record<number, typeof d.availability_schedules> = {};
    d.availability_schedules.forEach(s => {
      if (!byDay[s.day_of_week]) byDay[s.day_of_week] = [];
      byDay[s.day_of_week].push(s);
    });
    setSchedules(DAYS.map((_, i) => ({
      day_of_week: i,
      enabled: !!byDay[i],
      slots: byDay[i]
        ? byDay[i].map(s => ({ start_time: toHHMM(s.start_time), end_time: toHHMM(s.end_time), slot_duration_minutes: s.slot_duration_minutes ?? 15 }))
        : [{ start_time: "09:00", end_time: "17:00", slot_duration_minutes: 15 }],
    })));
    setErrors({});
  }, [isOpen, source]);

  if (!isOpen) return null;

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (form.specializations.length === 0) e.specializations = "At least one specialization is required";
    if (!form.department) e.department = "Department is required";
    for (const s of schedules) {
      if (!s.enabled) continue;
      for (const slot of s.slots) {
        if (slot.end_time <= slot.start_time) {
          e.schedule = `${DAYS[s.day_of_week]}: end time must be after start time`;
          return e;
        }
      }
      const sorted = [...s.slots].sort((a, b) => a.start_time.localeCompare(b.start_time));
      for (let i = 1; i < sorted.length; i++) {
        if (sorted[i].start_time < sorted[i - 1].end_time) {
          e.schedule = `${DAYS[s.day_of_week]}: time windows overlap`;
          return e;
        }
      }
    }
    return e;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setLoading(true);
    try {
      const payload: DoctorUpdatePayload = {
        name: form.name.trim(),
        specialization:  form.specializations.join(", "),
        specializations: form.specializations,
        department: form.department,
        status: form.status,
        registration_number: form.registration_number || undefined,
        qualifications: form.qualifications || undefined,
        experience_years: form.experience_years ? parseInt(form.experience_years) : undefined,
        consultation_fee: form.consultation_fee ? parseInt(form.consultation_fee) : undefined,
        room_location: form.room_location || undefined,
        phone: form.phone || undefined,
        // A locked email is the doctor's verified login — never send it for update.
        email: emailLocked ? undefined : form.email || undefined,
        availability_schedules: schedules.filter(s => s.enabled).flatMap(s =>
          s.slots.map(slot => ({
            day_of_week: s.day_of_week,
            start_time: `${slot.start_time}:00`,
            end_time: `${slot.end_time}:00`,
            slot_duration_minutes: slot.slot_duration_minutes,
          }))
        ),
      };
      const id = normalise(doctor as DoctorLike).id;
      await DoctorAPI.update(id, payload);
      toast.success("Doctor updated successfully");
      onSuccess();
    } catch (err: any) {
      toast.error(err.message || "Failed to update doctor");
    } finally {
      setLoading(false);
    }
  };

  const toggleDay = (dayIdx: number, enabled: boolean) =>
    setSchedules(prev => prev.map((s, i) => i === dayIdx ? { ...s, enabled } : s));

  const updateSlot = (dayIdx: number, slotIdx: number, field: keyof DaySlot, value: any) =>
    setSchedules(prev => prev.map((s, i) => i !== dayIdx ? s : {
      ...s, slots: s.slots.map((sl, j) => j === slotIdx ? { ...sl, [field]: value } : sl),
    }));

  const addSlot = (dayIdx: number) =>
    setSchedules(prev => prev.map((s, i) => i !== dayIdx ? s : (() => {
      const last = s.slots[s.slots.length - 1];
      const [h, m] = last.end_time.split(":").map(Number);
      const endH = Math.min(h + 1, 23);
      const pad = (n: number) => String(n).padStart(2, "0");
      return { ...s, slots: [...s.slots, { start_time: last.end_time, end_time: `${pad(endH)}:${pad(m)}`, slot_duration_minutes: last.slot_duration_minutes }] };
    })()));

  const removeSlot = (dayIdx: number, slotIdx: number) =>
    setSchedules(prev => prev.map((s, i) => i !== dayIdx ? s : {
      ...s, slots: s.slots.filter((_, j) => j !== slotIdx),
    }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full min-w-0 max-w-2xl max-h-[90vh] overflow-y-auto mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Edit Doctor</h2>
            <p className="text-sm text-gray-500">Update doctor details and schedule</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 min-h-[44px] min-w-[44px] flex items-center justify-center text-2xl leading-none">×</button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

            {/* Full Name — col-span-2 */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                className={`w-full border rounded-lg px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 ${errors.name ? "border-red-400" : "border-gray-200"}`}
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
              />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                className={`w-full border border-gray-200 rounded-lg px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 ${emailLocked ? "bg-gray-50 text-gray-500 cursor-not-allowed" : ""}`}
                value={form.email}
                disabled={emailLocked}
                title={emailLocked ? "This email is the doctor's verified login and cannot be changed." : undefined}
                onChange={e => setForm({ ...form, email: e.target.value })}
              />
              {emailLocked && (
                <p className="text-xs text-gray-400 mt-1">Verified login email — cannot be changed.</p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                value={form.phone}
                onChange={e => setForm({ ...form, phone: e.target.value })}
              />
            </div>

            {/* Specialization */}
            <div>
              <SpecializationTagInput
                label="Specialization *"
                value={form.specializations}
                onChange={(tags) => setForm({ ...form, specializations: tags })}
                error={errors.specializations}
              />
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                value={form.status}
                onChange={e => setForm({ ...form, status: e.target.value })}
              >
                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            {/* Department — col-span-2 so width matches other full-width fields */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Department <span className="text-red-500">*</span>
              </label>
              <select
                className={`w-full border rounded-lg px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 ${errors.department ? "border-red-400" : "border-gray-200"}`}
                value={form.department}
                onChange={e => setForm({ ...form, department: e.target.value })}
              >
                <option value="">Select department</option>
                {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
              {errors.department && <p className="text-red-500 text-xs mt-1">{errors.department}</p>}
            </div>

            {/* MCI Registration */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">MCI Registration No.</label>
              <input
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                value={form.registration_number}
                onChange={e => setForm({ ...form, registration_number: e.target.value })}
              />
            </div>

            {/* Qualifications */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Qualifications</label>
              <input
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                value={form.qualifications}
                onChange={e => setForm({ ...form, qualifications: e.target.value })}
              />
            </div>

            {/* Experience */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Experience (years)</label>
              <input
                type="number"
                min="0"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                value={form.experience_years}
                onChange={e => setForm({ ...form, experience_years: e.target.value })}
              />
            </div>

            {/* Consultation Fee */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Consultation Fee (₹)</label>
              <input
                type="number"
                min="0"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                value={form.consultation_fee}
                onChange={e => setForm({ ...form, consultation_fee: e.target.value })}
              />
            </div>

            {/* Room / Location — col-span-2 */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Room / Location</label>
              <input
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                value={form.room_location}
                onChange={e => setForm({ ...form, room_location: e.target.value })}
              />
            </div>

          </div>

          {/* Availability Schedule */}
          <div>
            <div className="flex items-baseline gap-2 mb-3">
              <h3 className="text-sm font-semibold text-gray-800">Availability Schedule</h3>
              <span className="text-xs text-gray-400">24-hour format</span>
            </div>
            {errors.schedule && <p className="text-red-500 text-xs mb-2">{errors.schedule}</p>}
            <div className="space-y-2">
              {schedules.map((s, dayIdx) => (
                <div key={dayIdx} className={`rounded-lg ${s.enabled ? "bg-blue-50" : "bg-gray-50"}`}>
                  <div className="flex items-center gap-3 py-1.5 px-2">
                    <input
                      type="checkbox"
                      checked={s.enabled}
                      onChange={e => toggleDay(dayIdx, e.target.checked)}
                      className="rounded w-4 h-4 accent-blue-500 cursor-pointer"
                    />
                    <span className={`text-sm w-24 font-medium ${s.enabled ? "text-gray-800" : "text-gray-400"}`}>
                      {DAYS[dayIdx]}
                    </span>
                    {s.enabled && (
                      <button
                        type="button"
                        onClick={() => addSlot(dayIdx)}
                        className="ml-auto text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                      >
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14"/></svg>
                        Window
                      </button>
                    )}
                  </div>
                  {s.slots.map((slot, slotIdx) => (
                    <div key={slotIdx} className="flex flex-wrap items-center gap-x-3 gap-y-2 px-2 pb-2 pl-9">
                      <input
                        type="time"
                        value={slot.start_time}
                        disabled={!s.enabled}
                        onChange={e => updateSlot(dayIdx, slotIdx, "start_time", e.target.value)}
                        className="border border-gray-200 rounded-lg px-3 py-1.5 text-base sm:text-sm bg-white disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-300 w-32"
                      />
                      <span className="text-gray-400 text-sm">to</span>
                      <input
                        type="time"
                        value={slot.end_time}
                        disabled={!s.enabled}
                        onChange={e => updateSlot(dayIdx, slotIdx, "end_time", e.target.value)}
                        className="border border-gray-200 rounded-lg px-3 py-1.5 text-base sm:text-sm bg-white disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-300 w-32"
                      />
                      <select
                        value={slot.slot_duration_minutes}
                        disabled={!s.enabled}
                        onChange={e => updateSlot(dayIdx, slotIdx, "slot_duration_minutes", parseInt(e.target.value))}
                        className="border border-gray-200 rounded-lg px-2 py-1.5 text-base sm:text-sm bg-white disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-300"
                      >
                        {[10, 15, 20, 30, 45, 60].map(m => <option key={m} value={m}>{m} min</option>)}
                      </select>
                      {s.slots.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeSlot(dayIdx, slotIdx)}
                          className="text-gray-300 hover:text-red-400 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                          title="Remove window"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
            <button type="button" onClick={onClose} className="px-4 min-h-[44px] text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={loading || fetching} className="px-5 min-h-[44px] text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 disabled:opacity-60">
              {loading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditDoctorModal;